<template lang="pug">
div(class="global-search-container")
  .input-group
    input.form-control(
      type="text"
      v-model="searchQuery"
      @input="debounceSearch"
      :placeholder="$I18n.t('vue.global_search.placeholder')"
      @focus="showResults = true"
    )
    .input-group-append
      button.btn.btn-primary(type="button" @click="performSearch")
        i.fa-solid.fa-search

  // Results Dropdown
  .search-results(v-if="showResults && hasResults" v-click-outside="closeResults")
    .result-section(v-if="limitedResults.orders && limitedResults.orders.length")
      h6.result-header(v-text="$I18n.t('vue.global_search.orders')")
      .result-item(v-for="order in limitedResults.orders" :key="'order-' + order.id")
        a(:href="order.url" @click="closeResults")
          .result-content
            strong 
              | Order #
              span(v-text="order.id")
            span.badge.ms-2(:class="getStatusBadgeClass(order.status)" v-text="order.status")
            .text-muted.small 
              span(v-text="order.subsidary")
              |  - 
              span(v-text="order.customer")

    .result-section(v-if="limitedResults.invoices && limitedResults.invoices.length")
      h6.result-header(v-text="$I18n.t('vue.global_search.invoices')")
      .result-item(v-for="invoice in limitedResults.invoices" :key="'invoice-' + invoice.id")
        a(:href="invoice.url" @click="closeResults")
          .result-content
            strong 
              | Invoice #
              span(v-text="invoice.document_number || invoice.id")
            span.badge.ms-2(:class="getStatusBadgeClass(invoice.status)" v-text="invoice.status")
            .text-muted.small 
              | $
              span(v-text="invoice.amount")

    .result-section(v-if="limitedResults.subsidaries && limitedResults.subsidaries.length")
      h6.result-header(v-text="$I18n.t('vue.global_search.restaurants')")
      .result-item(v-for="subsidary in limitedResults.subsidaries" :key="'subsidary-' + subsidary.id")
        a(:href="subsidary.url" @click="closeResults")
          .result-content
            strong(v-text="subsidary.name")
            .text-muted.small(v-text="subsidary.customer")

    .result-section(v-if="limitedResults.customers && limitedResults.customers.length")
      h6.result-header(v-text="$I18n.t('vue.global_search.customers')")
      .result-item(v-for="customer in limitedResults.customers" :key="'customer-' + customer.id")
        a(:href="customer.url" @click="closeResults")
          .result-content
            strong(v-text="customer.name")
            .text-muted.small(v-if="customer.aka")
              | AKA: 
              span(v-text="customer.aka")

    .result-section(v-if="limitedResults.vendors && limitedResults.vendors.length")
      h6.result-header(v-text="$I18n.t('vue.global_search.vendors')")
      .result-item(v-for="vendor in limitedResults.vendors" :key="'vendor-' + vendor.id")
        a(:href="vendor.url" @click="closeResults")
          .result-content
            strong(v-text="vendor.name")

    .result-section(v-if="limitedResults.payments && limitedResults.payments.length")
      h6.result-header(v-text="$I18n.t('vue.global_search.payments')")
      .result-item(v-for="payment in limitedResults.payments" :key="'payment-' + payment.id")
        a(:href="payment.url" @click="closeResults")
          .result-content
            strong 
              | Payment #
              span(v-text="payment.id")
            span.badge.ms-2(:class="getStatusBadgeClass(payment.status)" v-text="payment.status")
            .text-muted.small 
              | $
              span(v-text="payment.amount")

    .result-section(v-if="limitedResults.groups && limitedResults.groups.length")
      h6.result-header(v-text="$I18n.t('vue.global_search.groups')")
      .result-item(v-for="group in limitedResults.groups" :key="'group-' + group.id")
        a(:href="group.url" @click="closeResults")
          .result-content
            strong(v-text="group.name")
            .text-muted.small(v-if="group.supervisor")
              | Supervisor: 
              span(v-text="group.supervisor")

    .text-center.py-2.border-top(v-if="totalResults > displayedResults")
      small.text-muted 
        | {{ $I18n.t('vue.global_search.showing') }} 
        span(v-text="displayedResults")
        |  {{ $I18n.t('vue.global_search.of') }} 
        span(v-text="totalResults")
        |  {{ $I18n.t('vue.global_search.results') }}
      p.tw-mt-2
        small.text-primary.tw-cursor-pointer(@click="expandResults") 
          i.fa-solid.fa-angles-down.mr-1
          | {{ $I18n.t('vue.global_search.show_more') }}

  .search-results.empty(v-if="showResults && searchQuery && !hasResults && !loading")
    .text-center.py-3
      i.fa-solid.fa-search.fa-2x.text-muted
      p.text-muted.mt-2(v-text="$I18n.t('vue.global_search.no_results')")
</template>

<script>
const { BackendService } = require('app/services');

export default {
  data() {
    return {
      searchQuery: '',
      results: {},
      showResults: false,
      loading: false,
      debounceTimer: null,
      totalResults: 0,
      maxResultsPerSection: 10  // Límite de resultados por sección
    };
  },
  computed: {
    hasResults() {
      return Object.values(this.results).some(arr => arr && arr.length > 0);
    },
    displayedResults() {
      return Object.values(this.results).reduce((sum, arr) => sum + (arr?.length || 0), 0);
    },
    // Resultados limitados para cada sección
    limitedResults() {
      const limited = {};
      Object.keys(this.results).forEach(key => {
        if (this.results[key] && Array.isArray(this.results[key])) {
          limited[key] = this.results[key].slice(0, this.maxResultsPerSection);
        }
      });
      return limited;
    }
  },
  methods: {
    debounceSearch() {
      clearTimeout(this.debounceTimer);
      if (this.searchQuery.length >= 2) {
        this.debounceTimer = setTimeout(() => {
          this.performSearch();
        }, 300);
      } else {
        this.results = {};
        this.showResults = false;
      }
    },
    performSearch() {
      if (this.searchQuery.length < 2) return;

      this.loading = true;
      BackendService.get(`/search/global.json?q=${encodeURIComponent(this.searchQuery)}`)
        .then(response => {
          // BackendService.get() ya parsea JSON, response ES el objeto data
          this.results = response.results || [];
          this.totalResults = response.total_results || 0;
          this.showResults = true;
          this.loading = false;
        })
        .catch(error => {
          console.error('Search error:', error);
          this.loading = false;
        });
    },
    expandResults() {
      // Aumentar el límite de resultados
      this.maxResultsPerSection = this.maxResultsPerSection + 10;
    },
    closeResults() {
      setTimeout(() => {
        this.showResults = false;
        // Resetear el límite cuando se cierra
        this.maxResultsPerSection = 10;
      }, 200);
    },
    getStatusBadgeClass(status) {
      const statusClasses = {
        'REGISTERED': 'badge-secondary',
        'SCHEDULED': 'badge-primary',
        'COMPLETED': 'badge-success',
        'CANCELED': 'badge-danger',
        'UNPAID': 'badge-warning',
        'PAID': 'badge-success',
        'SEMI_PAID': 'badge-info'
      };
      return statusClasses[status] || 'badge-secondary';
    }
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  }
};
</script>

<style scoped>
.global-search-container {
  position: relative;
  width: 100%;
  max-width: 700px;
  min-width: 250px; /* Bug 2 fix: Prevents horizontal overflow on small phones */
}

.global-search-container .input-group {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}

.global-search-container .form-control {
  height: 42px;
  font-size: 0.95rem;
  border: 1px solid #e5e7eb;
  border-right: none;
  padding-left: 16px;
}

.global-search-container .form-control:focus {
  border-color: #3b82f6;
  box-shadow: none;
}

.global-search-container .btn {
  height: 42px;
  padding: 0 16px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-height: 500px;
  overflow-y: auto;
  z-index: 999;
  margin-top: 8px;
}

.result-section {
  border-bottom: 1px solid #eee;
  padding: 10px;
}

.result-section:last-child {
  border-bottom: none;
}

.result-header {
  font-size: 0.85rem;
  text-transform: uppercase;
  color: #666;
  margin-bottom: 8px;
  font-weight: 600;
}

.result-item {
  padding: 12px 8px; /* Mejora 2: Aumentar padding vertical para áreas táctiles móviles */
  border-radius: 4px;
  margin-bottom: 4px;
}

.result-item:hover {
  background-color: #f8f9fa;
}

.result-item a {
  text-decoration: none;
  color: inherit;
  display: block;
}

.result-content {
  line-height: 1.5;
}

.search-results.empty {
  padding: 20px;
}
</style>
